import type { ColumnSort, PaginationState } from '@tanstack/react-table';
import type { InferRequestType, InferResponseType } from 'hono/client';
import { type ApiClient, useFetch } from '../api';
import { sortMapper } from '../common/sort_mapper';

type GetMyWorkspaces = ApiClient['v2']['me']['workspaces']['$get'];
type GetAllWorkspaces = ApiClient['v2']['workspaces']['$get'];
type GetWorkspace = ApiClient['v2']['workspaces'][':workspaceId']['$get'];

export type MyWorkspaceListResponse = InferResponseType<GetMyWorkspaces, 200>;
export type WorkspaceListResponse = InferResponseType<GetAllWorkspaces, 200>;
export type WorkspaceResponse = InferResponseType<GetWorkspace, 200>;

type ListWorkspacesQuery = NonNullable<InferRequestType<GetAllWorkspaces>['query']>;
type ListMyWorkspacesQuery = NonNullable<InferRequestType<GetMyWorkspaces>['query']>;

type WorkspaceQuery<TQuery> = {
  filters?: Omit<TQuery, 'sort' | 'limit' | 'offset'>;
  pagination?: PaginationState;
  sort?: ColumnSort;
};

export type MyWorkspacesQuery = WorkspaceQuery<ListMyWorkspacesQuery>;
export type AllWorkspacesQuery = WorkspaceQuery<ListWorkspacesQuery>;

const serializeMyWorkspacesQuery = (query: MyWorkspacesQuery): ListMyWorkspacesQuery => ({
  ...(query.filters ?? {}),
  ...(query.pagination
    ? {
        limit: query.pagination.pageSize.toString(),
        offset: (query.pagination.pageSize * query.pagination.pageIndex).toString(),
      }
    : {}),
  ...(query.sort
    ? {
        sort: sortMapper<ListMyWorkspacesQuery & { sort?: unknown[] }>(query.sort),
      }
    : {}),
});

const serializeAllWorkspacesQuery = (query: AllWorkspacesQuery): ListWorkspacesQuery => ({
  ...(query.filters ?? {}),
  ...(query.pagination
    ? {
        limit: query.pagination.pageSize.toString(),
        offset: (query.pagination.pageSize * query.pagination.pageIndex).toString(),
      }
    : {}),
  ...(query.sort
    ? {
        sort: sortMapper<ListWorkspacesQuery & { sort?: unknown[] }>(query.sort),
      }
    : {}),
});

export function useWorkspaces(query: MyWorkspacesQuery | null = {}) {
  return useFetch<MyWorkspaceListResponse>(
    query ? `workspaces/me?${JSON.stringify(query)}` : null,
    (client) =>
      client.v2.me.workspaces.$get({
        query: serializeMyWorkspacesQuery(query ?? {}),
      })
  );
}

export function useAllWorkspaces(query: AllWorkspacesQuery = {}) {
  return useFetch<WorkspaceListResponse>(`workspaces?${JSON.stringify(query)}`, (client) =>
    client.v2.workspaces.$get({
      query: serializeAllWorkspacesQuery(query),
    })
  );
}
